import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StyledButton } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { colorPalette, fontFamily, mediaQueriesBP } from "@waytek/Utilities/variables";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    title = "title",
    description = "description",
    topicsTitle = "topicsTitle",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.topicsTitle]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BlogLandingWrapperStyles {
    headerWrapper?: InjectableCss;
    infoContainer?: GridContainerProps;
    infoItem?: GridItemProps;
    infoItemContainer?: InjectableCss;
    topicsItem?: GridItemProps;
    topicsItemMobile?: GridItemProps;
    topicsItemContainer?: InjectableCss;
    recentPostsWrapper?: InjectableCss;
    dividerTitle?: InjectableCss;
    dividerTopic?: InjectableCss;
    titleTypo?: TypographyPresentationProps;
    descriptionTypo?: TypographyPresentationProps;
    topicTypo?: TypographyPresentationProps;
}

const topicItemsBase = css`
    display: flex;
    flex-direction: column;

    .link-list__widget {
        padding: 0;

        & > div {
            padding: 0;
            margin-bottom: 11px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        ${StyledButton} {
            ${TypographyStyle} {
                text-transform: none;
                font-size: 16px;
                font-weight: normal;
                font-stretch: normal;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
            }
        }
    }
`;

const dividerBase = css`
    width: 100%;
    height: 4px;
    background: ${colorPalette.common.global.industrialTaupe};
    margin: 0;
`;

export const blogLandingWrapperStyles: BlogLandingWrapperStyles = {
    infoContainer: {
        gap: 40,
    },
    infoItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    infoItemContainer: {
        css: css`
            ${mediaQueriesBP.minMD} {
                padding-right: 60px;
            }
        `,
    },
    titleTypo: {
        lineHeight: "normal",
        weight: 500,
        fontFamily,
        css: css`
            font-size: 28px;

            ${mediaQueriesBP.minMD} {
                font-size: 36px;
            }
        `,
    },
    descriptionTypo: {
        lineHeight: 1.5,
        css: css`
            font-size: 14px;

            ${mediaQueriesBP.minMD} {
                font-size: 16px;
            }

            p {
                font-size: 14px;

                ${mediaQueriesBP.minMD} {
                    font-size: 16px;
                }
            }

            > * {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    topicTypo: {
        lineHeight: 1.2,
        fontFamily,
        weight: "normal",
        css: css`
            font-size: 21px;
            padding: 0;

            ${mediaQueriesBP.minMD} {
                margin-top: 22px;
                padding: 0 20px;
            }
        `,
    },
    topicsItem: {
        width: 4,
        css: css`
            ${topicItemsBase}

            ${mediaQueriesBP.maxMD} {
                display: none;
            }
        `,
    },
    topicsItemMobile: {
        width: 12,
        css: css`
            ${topicItemsBase}

            ${mediaQueriesBP.minMD} {
                display: none;
            }
        `,
    },
    topicsItemContainer: {
        css: css`
            ${mediaQueriesBP.minMD} {
                padding: 0 20px;
            }
        `,
    },
    dividerTitle: {
        css: css`
            ${dividerBase}

            margin-top: 5px;
            margin-bottom: 17px;

            ${mediaQueriesBP.minMD} {
                margin-top: 10px;
                margin-bottom: 28px;
            }
        `,
    },
    dividerTopic: {
        css: css`
            ${dividerBase}

            margin-top: 6px;
            margin-bottom: 20px;

            ${mediaQueriesBP.minMD} {
                margin-top: 6px;
                margin-bottom: 16px;
            }
        `,
    },
};

const styles = blogLandingWrapperStyles;

const BlogLandingWrapper: FC<Props> = ({ id, fields, pageTitle }) => {
    return (
        <>
            <StyledWrapper {...styles.headerWrapper}>
                <Zone contentId={id} zoneName="ContentBLW01" />
            </StyledWrapper>

            <GridContainer {...styles.infoContainer}>
                <GridItem {...styles.infoItem}>
                    <Typography as="h1" variant="body" {...styles.titleTypo} data-test-selector="PageTitle">
                        {fields.title || pageTitle}
                    </Typography>
                    <StyledWrapper {...styles.dividerTitle} />

                    <StyledWrapper {...styles.infoItemContainer}>
                        <Typography {...styles.descriptionTypo}>{parse(fields.description, parserOptions)}</Typography>
                        <Zone contentId={id} zoneName="ContentBLW02" />
                    </StyledWrapper>
                </GridItem>

                <GridItem {...styles.topicsItem}>
                    <Typography as="h3" variant="body" {...styles.topicTypo}>
                        {fields.topicsTitle}
                    </Typography>
                    <StyledWrapper {...styles.dividerTopic} />

                    <StyledWrapper {...styles.topicsItemContainer}>
                        <Zone contentId={id} zoneName="ContentBLW03" />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>

            <StyledWrapper {...styles.recentPostsWrapper}>
                <Zone contentId={id} zoneName="ContentBLW04" />
            </StyledWrapper>

            <StyledWrapper {...styles.topicsItemMobile}>
                <Typography as="h3" variant="body" {...styles.topicTypo}>
                    {fields.topicsTitle}
                </Typography>
                <StyledWrapper {...styles.dividerTopic} />

                <StyledWrapper {...styles.topicsItemContainer}>
                    <Zone contentId={id} zoneName="ContentBLW03" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BlogLandingWrapper),
    definition: {
        displayName: "Blog Landing Page",
        group: "Blog" as any,
        allowedContexts: ["BlogLandingPage" as any],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Override Page Title",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Page Description",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.topicsTitle,
                displayName: "Topics Title",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
